import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { MessageIcon } from '../../../components/shared-components/message-icon';
import { SPEC_PATHS } from '../../../components/shared-components/listing-icons';
import L from 'leaflet';
import { MapContainer, Marker, Popup, TileLayer, ZoomControl } from 'react-leaflet';
import { useMap } from 'react-leaflet/hooks';
import 'leaflet/dist/leaflet.css';
import { ConfigContext } from '../../../contexts/config-context';
import { LikeButton } from '../../main-page/components/featured-listings';
import { formatNumber } from '../../main-page/home-utils';
import { listingPhoto } from './result-card';
import { ICON_PATHS } from '../../../components/shared-components/icon-paths';
import { numberLabel, plotAreaLabel } from '../../../components/shared-components/listing-forms';

const DEFAULT_CENTER = [44.8125, 20.4612];
const DEFAULT_ZOOM = 12;
const MAX_FIT_ZOOM = 14;
const POPUP_WIDTH = 252;
const TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
// Esri has no tiles of this style past this zoom; closer in, the last level is stretched
const TILES_MAX_ZOOM = 16;
// floors without a number, shortened the way listings usually write them
const FLOOR_SHORT = { basement_level: 'SU', lower_ground: 'PS', ground: 'PR', higher_ground: 'VPR' };

const shortPrice = (price) => price >= 1000 ? `${Math.round(price / 1000)}k €` : price > 0 ? `${formatNumber(price)} €` : '•';

const FitToPins = ({ points, layoutKey }) => {
    const map = useMap();

    useEffect(() => {
        // the container changes size when the view is switched
        map.invalidateSize();
        if (points.length > 0) {
            map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: MAX_FIT_ZOOM });
        }
    }, [map, points, layoutKey]);

    return null;
};

const Spec = ({ title, value, children }) => (
    <span className='hs-map-card-spec' title={title}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
        <span>{value}</span>
    </span>
);

const PopupCard = ({ listing, onMessage, onHide }) => {
    const { api, floor_options } = useContext(ConfigContext);
    const { t } = useTranslation(["results", "listing"]);
    const navigate = useNavigate();
    const [photo, setPhoto] = useState(0);

    const total = listing.photos?.length || 0;
    const price = parseFloat(listing.price) || 0;
    const rooms = numberLabel('num_rooms', listing.num_rooms);
    const baths = numberLabel('num_bathrooms', listing.num_bathrooms);
    const area = Number(listing.area);
    const plot = !area && plotAreaLabel(listing);
    const floorLabel = floor_options.find(o => o.id === listing.floor)?.label;
    const floor = FLOOR_SHORT[floorLabel] || floorLabel;
    const open = () => navigate(`/oglas/${listing.id}`);
    const step = (dir) => (e) => {
        e.stopPropagation();
        setPhoto((photo + dir + total) % total);
    };

    return (
        <div className='hs hs-map-card'>
            <div className='hs-map-card-photo' style={{ backgroundImage: `url(${listingPhoto(listing, api, photo)})` }} onClick={open}>
                {total > 1 && <>
                    <button className='hs-map-card-arrow prev' onClick={step(-1)} aria-label={t('prev_photo')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                    </button>
                    <button className='hs-map-card-arrow next' onClick={step(1)} aria-label={t('next_photo')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                    </button>
                </>}
                {listing.promoted && <span className='hs-map-card-badge'>{listing.promoted_value?.label || 'Premium'}</span>}
                {total > 0 && <span className='hs-map-card-count'>{photo + 1}/{total}</span>}
            </div>
            <div className='hs-map-card-body' onClick={open}>
                <span className='hs-map-card-price'>{price > 0 ? `${formatNumber(price)} €` : t('price_placeholder', { ns: 'listing' })}</span>
                <div className='hs-map-card-specs'>
                    {rooms && <Spec title={t('num_rooms', { ns: 'listing' })} value={rooms}>
                        {SPEC_PATHS.rooms}
                    </Spec>}
                    {(area > 0 || plot) && <Spec title={t(area > 0 ? 'area' : 'plot_area', { ns: 'listing' })} value={area > 0 ? `${formatNumber(area)} m²` : plot}>
                        {SPEC_PATHS.area}
                    </Spec>}
                    {baths && <Spec title={t('num_bathrooms', { ns: 'listing' })} value={baths}>
                        {SPEC_PATHS.baths}
                    </Spec>}
                    {floor && <Spec title={t('floor', { ns: 'listing' })} value={floor}>
                        {SPEC_PATHS.floor}
                    </Spec>}
                </div>
            </div>
            <div className='hs-map-card-actions'>
                <button className='hs-map-card-msg' onClick={() => onMessage(listing)}>
                    <MessageIcon />
                    {t('message')}
                </button>
                <span className='hs-spacer'></span>
                <button className='hs-map-card-hide' onClick={() => onHide(listing.id)} aria-label={t('hide_listing')} title={t('hide_listing')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={ICON_PATHS.eyeOff} /></svg>
                </button>
                <LikeButton listing={listing.id} followers={listing.followers} className='hs-map-card-like' />
            </div>
        </div>
    );
};

const Pin = ({ listing, position, active, onHover, onMessage, onHide }) => {
    const markerRef = useRef(null);
    const label = shortPrice(parseFloat(listing.price) || 0);
    const promoted = !!listing.promoted;

    // the icon is only rebuilt when its content changes; replacing it on every
    // hover would swap the element under the cursor
    const icon = useMemo(() => L.divIcon({
        className: 'hs-pin-anchor',
        iconSize: [0, 0],
        html: `<span class="hs-pin ${promoted ? 'premium' : ''}">${label}</span>`,
    }), [label, promoted]);

    useEffect(() => {
        const pin = markerRef.current?.getElement()?.querySelector('.hs-pin');
        if (pin) pin.classList.toggle('active', active);
    }, [active, icon]);

    // an open popup pans the map back to itself whenever it gets new children, so the card
    // has to stay the same element while the map re-renders on hover
    const card = useMemo(() => <PopupCard listing={listing} onMessage={onMessage} onHide={onHide} />, [listing, onMessage, onHide]);

    return (
        <Marker ref={markerRef} position={position} icon={icon} zIndexOffset={active ? 1000 : promoted ? 500 : 0}
            eventHandlers={{ mouseover: () => onHover(listing.id), mouseout: () => onHover(null) }}>
            <Popup className='hs-map-popup' closeButton={false} offset={[0, -26]} minWidth={POPUP_WIDTH} maxWidth={POPUP_WIDTH}
                autoPanPadding={[12, 12]}>
                {card}
            </Popup>
        </Marker>
    );
};

export const SearchMap = ({ listings, hidden, activeId, onHover, onMessage, onHide, layoutKey }) => {
    const { t } = useTranslation(["results"]);

    // the map is only refitted when the search results themselves change; `listings` must keep
    // its identity between renders, otherwise every hover would undo the visitor's zoom
    const located = useMemo(() => listings
        .map(listing => ({ listing, position: [parseFloat(listing.lat), parseFloat(listing.lng)] }))
        .filter(pin => Number.isFinite(pin.position[0]) && Number.isFinite(pin.position[1])), [listings]);
    const points = useMemo(() => located.map(pin => pin.position), [located]);
    const pins = located.filter(pin => !hidden[pin.listing.id]);

    return (
        <div className='hs-map'>
            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} zoomControl={false} scrollWheelZoom={true}>
                <TileLayer url={TILES} attribution='&copy; Esri' maxNativeZoom={TILES_MAX_ZOOM} />
                <ZoomControl position='topright' />
                <FitToPins points={points} layoutKey={layoutKey} />
                {pins.map(({ listing, position }) => (
                    <Pin key={`pin-${listing.id}`} listing={listing} position={position} active={listing.id === activeId}
                        onHover={onHover} onMessage={onMessage} onHide={onHide} />
                ))}
            </MapContainer>
            <div className='hs-map-count'>{t('on_map', { n: pins.length })}</div>
        </div>
    );
};
